import React, { memo, useMemo } from 'react'
import { BdaServiceProperty } from '../../models/BdaService';
import ScriptEditor from '../common/ScriptEditor';
import { Select } from 'antd';
import { isEqual } from 'lodash';

type Props = {
  prop: BdaServiceProperty;
  onPropChange: (key: string, checkbox?: boolean, pure?: boolean) => (evt: any) => void;
  onBlur?: VoidFunction;
  wideMode?: boolean;
}

const normalizePreset = (rawPreset: any, isJson: boolean) => {
  if (rawPreset === undefined || !rawPreset) return undefined;
  if (isJson) {
    try {
      return JSON.parse(rawPreset);
    }
    catch { }
  }
  return String(rawPreset);
}

const getPresetField = (rawPreset: any, isJson: boolean, field: "key" | "label" | "value") => {
  const normalized = normalizePreset(rawPreset, isJson);
  return typeof normalized === "string" ? normalized : normalized?.[field];
}

const presetsMatch = (current: any, rawPreset: any) => {
  if (typeof current === "object") {
    try {
      const presetObject = JSON.parse(rawPreset);
      return current.key === presetObject.key;
    } catch { }
  }
  return String(current) === String(rawPreset);
}

const findMatchingPreset = (presetValue: any, isJson: boolean, presets: any[]) => {
  // find preset with provided value
  const found = presets.find(preset => isEqual(isJson ? JSON.parse(presetValue) : presetValue, getPresetField(preset, isJson, "value")));
  try {
    return JSON.parse(found || null);
  } catch { }
  return presetValue;
}

function PresetValuesSelect({ prop, onPropChange, onBlur, wideMode }: Props) {
  const isJson = prop.valueType === "JSON";
  const presetValues = prop.presetValues!;
  const currentPreset = findMatchingPreset(prop.value || prop.defaultValue, isJson, presetValues);
  const selectedIndex = presetValues.findIndex(preset => presetsMatch(currentPreset, preset));

  const handleChange = (index?: number) => {
    const preset = index === undefined ? undefined : presetValues[index];
    let value = getPresetField(preset, isJson, "value");
    if (typeof value === "object") value = JSON.stringify(value, null, "\t");
    onPropChange(prop.key!, false, true)(value);
  }

  const presetValuesOptions = useMemo(() => {
    return (presetValues || []).map((preset, index) => ({ value: index, label: getPresetField(preset, isJson, "label") }));
  }, [presetValues, isJson]);

  return (
    <>
      <Select
        className="w-100 h-auto"
        value={selectedIndex >= 0 ? selectedIndex : undefined}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={`${prop.key} value`}
        allowClear={!prop.mandatory}
        options={presetValuesOptions}
      />
      {isJson && currentPreset && (
        <div className="mt-2">
          <ScriptEditor height={wideMode ? 450 : 150} value={currentPreset.value || currentPreset} readOnly />
        </div>
      )}
    </>
  )
}

export default memo(PresetValuesSelect);